import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  Chip,
  Typography,
  darken,
  lighten,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CourseCardMedia from '../courses/CourseCardMedia';
import { Course } from '../../../core/interfaces/course.interface';

interface MeCourseCardProps {
  course: Course;
  onSelect: (course: Course) => void;
}

const MeCourseCard = ({ course, onSelect }: MeCourseCardProps) => {
  const { t } = useTranslation();
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = useCallback(() => {
    const el = descriptionRef.current;
    if (!el || expanded) {
      return;
    }
    setIsTruncated(el.scrollHeight > el.clientHeight + 1);
  }, [expanded]);

  useLayoutEffect(() => {
    checkTruncation();
  }, [course.description, checkTruncation]);

  useLayoutEffect(() => {
    const el = descriptionRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(() => checkTruncation());
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkTruncation]);

  const toggleExpanded = (event: React.MouseEvent) => {
    event.stopPropagation();
    setExpanded((prev) => !prev);
  };

  const showExpand = isTruncated && !expanded;
  const showCollapse = expanded;

  return (
    <Card
      className={`flex flex-col shadow cursor-pointer overflow-hidden ${
        expanded ? 'min-h-[440px] h-auto' : 'h-[440px]'
      }`}
      onClick={() => onSelect(course)}
    >
      <CourseCardMedia course={course} />
      <CardContent className="flex flex-col flex-1 min-h-0 px-16 pt-16 pb-24">
        <div className="w-full flex flex-col flex-1 min-h-0">
          <div className="flex justify-between mb-12 shrink-0">
            <div className="flex flex-wrap">
              {course.categories.map((category) => (
                <Chip
                  className="font-semibold text-12 mx-2 my-2"
                  label={category.name}
                  sx={{
                    color: () => darken(category.color, 0.4),
                    backgroundColor: () => lighten(category.color, 0.8),
                  }}
                  size="small"
                  key={category.id}
                />
              ))}
            </div>
          </div>

          <Typography className="text-16 mt-2 font-medium shrink-0">
            {course.name}
          </Typography>

          {course.description ? (
            <>
              <Typography
                ref={descriptionRef}
                className="text-13 mt-8"
                color="text.secondary"
                sx={{
                  lineHeight: 1.45,
                  flex: expanded ? '0 1 auto' : 1,
                  minHeight: 0,
                  ...(expanded
                    ? {}
                    : {
                        display: '-webkit-box',
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }),
                }}
              >
                {course.description}
              </Typography>

              {(showExpand || showCollapse) && (
                <Button
                  size="small"
                  color="primary"
                  className="mt-8 self-start shrink-0"
                  sx={{ textTransform: 'none', fontWeight: 600, px: 0, minWidth: 0 }}
                  onClick={toggleExpanded}
                >
                  {showCollapse
                    ? t('courses.label.show_less')
                    : t('courses.label.show_more')}
                </Button>
              )}
            </>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

export default MeCourseCard;
